/**
 * Backlog data, the drag-to-sprint write path, and multi-select.
 *
 * Sprint membership is a plain column on the issue row, so moving an issue
 * between the backlog and a sprint is the same `POST /issues/{key}/move` the
 * board uses — with `sprint_id` set instead of `column_id`, and the same pair
 * of neighbours so the rank stays meaningful inside the destination.
 */

import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { UUID } from '@/types/api'

import {
  issueKeys,
  patchIssueInCaches,
  v2Api,
  type BacklogResponse,
  type Issue,
  type MoveIssueInput,
  type Sprint,
} from '@/features/issue'

/** One planning section: a sprint, or the loose backlog at the bottom. */
export interface BacklogSection {
  /** dnd container id — never an issue id, so the two cannot be confused. */
  key: string
  sprint: Sprint | null
  issues: Issue[]
  count: number
  points: number
}

export const BACKLOG_SECTION_KEY = 'group:backlog'

export function sectionKeyFor(sprintId: string | null | undefined): string {
  return sprintId ? `group:${sprintId}` : BACKLOG_SECTION_KEY
}

export function sprintIdFromSectionKey(key: string): UUID | null {
  const id = key.slice('group:'.length)
  return id === 'backlog' ? null : id
}

export function useBacklogQuery(
  slug: string | null,
  projectKey: string | null,
  includeCompleted: boolean,
) {
  return useQuery({
    queryKey: issueKeys.backlog(slug ?? '', projectKey ?? '', includeCompleted),
    queryFn: () => v2Api.backlog(slug as string, projectKey as string, includeCompleted),
    enabled: Boolean(slug && projectKey),
    staleTime: 15_000,
  })
}

export interface BacklogMove {
  issue: Issue
  toSprintId: UUID | null
  after: Issue | null
  before: Issue | null
}

/** Apply a move to a cached backlog response. */
export function applyBacklogMove(backlog: BacklogResponse, move: BacklogMove): BacklogResponse {
  const strip = (issues: Issue[]) => issues.filter((entry) => entry.id !== move.issue.id)
  const moved: Issue = { ...move.issue, sprint_id: move.toSprintId }

  const insert = (issues: Issue[]): Issue[] => {
    const next = [...issues]
    let position = next.length
    if (move.after) {
      const found = next.findIndex((entry) => entry.id === move.after!.id)
      if (found !== -1) position = found + 1
    } else if (move.before) {
      const found = next.findIndex((entry) => entry.id === move.before!.id)
      position = found === -1 ? 0 : found
    } else {
      position = 0
    }
    next.splice(position, 0, moved)
    return next
  }

  const points = (issues: Issue[]) =>
    issues.reduce((sum, entry) => sum + (entry.story_points ?? 0), 0)

  const sprints = backlog.sprints.map((group) => {
    const issues =
      group.sprint && group.sprint.id === move.toSprintId
        ? insert(strip(group.issues))
        : strip(group.issues)
    return { ...group, issues, count: issues.length, points: points(issues) }
  })

  const looseIssues =
    move.toSprintId === null
      ? insert(strip(backlog.backlog.issues))
      : strip(backlog.backlog.issues)

  return {
    ...backlog,
    sprints,
    backlog: {
      ...backlog.backlog,
      issues: looseIssues,
      count: looseIssues.length,
      points: points(looseIssues),
    },
  }
}

export function useBacklogMove(
  slug: string | null,
  projectKey: string | null,
  includeCompleted: boolean,
) {
  const client = useQueryClient()
  const [error, setError] = useState<unknown>(null)
  const backlogKey = issueKeys.backlog(slug ?? '', projectKey ?? '', includeCompleted)

  const mutation = useMutation({
    mutationFn: (move: BacklogMove) => {
      const input: MoveIssueInput = { sprint_id: move.toSprintId }
      if (move.after) input.after_id = move.after.issue_key ?? move.after.id
      if (move.before) input.before_id = move.before.issue_key ?? move.before.id
      return v2Api.moveIssue(slug as string, move.issue.issue_key ?? move.issue.id, input)
    },
    onMutate: async (move) => {
      setError(null)
      await client.cancelQueries({ queryKey: backlogKey })
      const snapshot = client.getQueryData<BacklogResponse>(backlogKey)
      client.setQueryData<BacklogResponse>(backlogKey, (backlog) =>
        backlog ? applyBacklogMove(backlog, move) : backlog,
      )
      return { snapshot }
    },
    onError: (mutationError, _move, context) => {
      if (context?.snapshot) client.setQueryData(backlogKey, context.snapshot)
      setError(mutationError)
    },
    onSuccess: (issue) => {
      if (slug) patchIssueInCaches(client, slug, issue)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: backlogKey })
      if (slug) {
        void client.invalidateQueries({ queryKey: issueKeys.boards(slug) })
        void client.invalidateQueries({ queryKey: issueKeys.sprints(slug, projectKey ?? '') })
      }
    },
  })

  return {
    move: mutation.mutate,
    isMoving: mutation.isPending,
    error,
    clearError: useCallback(() => setError(null), []),
  }
}

export interface Selection {
  selected: Set<string>
  isSelected: (id: string) => boolean
  count: number
  /** Click, ⌘/Ctrl-click and Shift-click, against the flat visible order. */
  onSelect: (id: string, modifiers: { shift: boolean; meta: boolean }) => void
  /** Adds or removes a whole group without disturbing the rest. */
  setMany: (ids: string[], on: boolean) => void
  clear: () => void
  selectAll: (ids: string[]) => void
}

/**
 * Multi-select over the flattened backlog.
 *
 * The order matters: a Shift-click selects the run between the anchor and the
 * clicked row *as displayed*, across sprint boundaries, which is what makes
 * "drag the bottom eight into the next sprint" a two-gesture operation.
 */
export function useSelection(orderedIDs: string[]): Selection {
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [anchor, setAnchor] = useState<string | null>(null)

  const order = useMemo(() => new Map(orderedIDs.map((id, index) => [id, index])), [orderedIDs])

  const onSelect = useCallback(
    (id: string, modifiers: { shift: boolean; meta: boolean }) => {
      setSelected((current) => {
        const next = new Set(current)
        if (modifiers.shift && anchor !== null && order.has(anchor) && order.has(id)) {
          const from = order.get(anchor)!
          const to = order.get(id)!
          const [start, end] = from <= to ? [from, to] : [to, from]
          for (let index = start; index <= end; index += 1) {
            const entry = orderedIDs[index]
            if (entry) next.add(entry)
          }
          return next
        }
        // A plain click TOGGLES. These are checkboxes, and a checkbox that
        // clears every other box when you tick it is not a checkbox — picking a
        // second issue silently dropped the first, so building a multi-issue
        // selection was impossible without knowing to hold ctrl or cmd.
        //
        // Modifiers still work and are no longer required: shift extends a
        // range from the anchor (above), and ctrl/cmd is now just a synonym for
        // the plain click rather than the only way in.
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
      if (!modifiers.shift) setAnchor(id)
    },
    [anchor, order, orderedIDs],
  )

  return {
    selected,
    isSelected: useCallback((id: string) => selected.has(id), [selected]),
    count: selected.size,
    onSelect,
    clear: useCallback(() => {
      setSelected(new Set())
      setAnchor(null)
    }, []),
    setMany: useCallback((ids: string[], on: boolean) => {
      setSelected((current) => {
        const next = new Set(current)
        for (const id of ids) {
          if (on) next.add(id)
          else next.delete(id)
        }
        return next
      })
    }, []),
    selectAll: useCallback((ids: string[]) => setSelected(new Set(ids)), []),
  }
}
